import * as React from 'react';
import { useAuth } from '@/auth/AuthProvider';
import { setLocale } from '@/i18n';
import { asRows, PROFILE_COLUMNS, toProfile } from './rows';
import { callRpc, type ActionResult } from './rpc';
import type { Profile } from './types';

/**
 * The caller's own profile (design section 3): the `user_profiles` row (RLS lets each user read only his
 * own; the `user_id` filter is still explicit), `null` while he has none yet. `save` sends
 * `profile_update { p_patch }` with only the changed keys, then reads again; a changed language also
 * switches the portal.
 *
 * Keyed on the user, not the agency: a profile belongs to the person.
 */

export interface ProfileValues {
  displayName: string;
  phone: string;
  locale: 'en' | 'es';
}

export interface ProfileState {
  status: 'loading' | 'error' | 'ready';
  /** Null when the row does not exist yet. */
  profile: Profile | null;
  save(values: ProfileValues): Promise<ActionResult>;
  reload(): void;
}

/**
 * The patch from the form to the stored row. The name is required and never cleared (a blank name
 * stays out); `''` over a stored phone clears it (null). With no row yet, every filled field goes.
 */
export function profilePatch(current: Profile | null, values: ProfileValues): Record<string, string | null> {
  const patch: Record<string, string | null> = {};
  const name = values.displayName.trim();
  const phone = values.phone.trim();
  if (name && name !== (current?.displayName ?? '')) patch.display_name = name;
  if (phone !== (current?.phone ?? '')) {
    if (phone) patch.phone = phone;
    else if (current?.phone) patch.phone = null;
  }
  if (!current || values.locale !== current.locale) patch.locale = values.locale;
  return patch;
}

const ProfileContext = React.createContext<ProfileState | null>(null);

type Loaded = { status: 'loading' } | { status: 'error' } | { status: 'ready'; profile: Profile | null };

export function ProfileProvider({ children }: { children: React.ReactNode }) {
  const { client, user } = useAuth();
  const userId = user?.id ?? null;
  const [loaded, setLoaded] = React.useState<Loaded>({ status: 'loading' });
  const [nonce, setNonce] = React.useState(0);
  const reload = React.useCallback(() => setNonce((n) => n + 1), []);

  React.useEffect(() => {
    if (!userId) {
      setLoaded({ status: 'loading' });
      return;
    }
    let active = true;
    void (async () => {
      const { data, error } = await client.from('user_profiles').select(PROFILE_COLUMNS).eq('user_id', userId);
      if (!active) return;
      if (error) {
        console.warn('ProfileProvider: could not read the profile', error);
        setLoaded({ status: 'error' });
        return;
      }
      const row = asRows(data)[0];
      setLoaded({ status: 'ready', profile: row ? toProfile(row) : null });
    })();
    return () => {
      active = false;
    };
  }, [client, userId, nonce]);

  const current = loaded.status === 'ready' ? loaded.profile : null;

  const save = React.useCallback<ProfileState['save']>(
    async (values) => {
      const patch = profilePatch(current, values);
      if (Object.keys(patch).length === 0) return { data: null, error: null };
      const res = await callRpc<void>(client, 'profile_update', { p_patch: patch });
      if (res.error) return res;
      if (patch.locale === 'en' || patch.locale === 'es') setLocale(patch.locale);
      reload();
      return res;
    },
    [client, current, reload],
  );

  const value = React.useMemo<ProfileState>(
    () => ({ status: loaded.status, profile: current, save, reload }),
    [loaded.status, current, save, reload],
  );

  return <ProfileContext.Provider value={value}>{children}</ProfileContext.Provider>;
}

export function useProfile(): ProfileState {
  const ctx = React.useContext(ProfileContext);
  if (!ctx) throw new Error('useProfile must be used inside ProfileProvider');
  return ctx;
}
